import Modal from "./modal";
import React, {useMemo} from "react";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import MultiSelectSort from "./sortable-select";
import {Grid} from "@mui/material";
import Typography from "@mui/material/Typography";
import RadioButtonsGroup from "./radio-group/radio-group";
import {
    INVOICE_BOLD_FIELD_OPTIONS,
    INVOICE_HIDDEN_FIELD_OPTIONS,
    resolveInvoiceHiddenFields,
} from "../utils/invoice-bold-fields";
import {ADMIN_SECTION_DEFINITIONS} from "../utils/admin-sections";

const PRIMARY_ROLES = {
    superadmin: "SuperAdmin",
    admin: "Admin",
    manager: "Manager",
    clerk: "Clerk",
    approver: "Approver",
    assistant: "Assistant"
};

const DEFAULT_EXPENSE_EDITOR_VARIANT = "normal";
const DEFAULT_INVOICE_EDITOR_VARIANT = "normal";
const DEFAULT_CALC_MODE = "auto_adjusted";
const EXPENSE_VIEW_OPTIONS = ["review", "controller"];
const optionNameOf = (option) => typeof option === "string" ? option : option?.name;

const DEFAULT_ACTIONS_BY_COMPONENT = {
    monitor: ["monitorHideUserSelectorFor", "canAccessVendorFingerprintMonitoring"],
    payments: [],
    data: [],
    cashflow: [],
    workflow: [],
    admin: ADMIN_SECTION_DEFINITIONS.map((section) => section.action),
    invoices: ["canValidateInvoices", "canFinalizeInvoices", "canRestartProcessing", "canDeleteInvoice", "canSeeInvoiceStatusFilter"],
    expenses: [
        "canAccessExpenseConfigurations",
        "canAccessExpenseRules",
        "canValidateExpense",
        "canCorrectValidatedExpense",
        "canReturnExpenseReport"
    ],
    reporting: ["canAccessReports"],
    approval: ["canApproveOrDeny", "canRequestApproval"],
    "detail-approval": ["canRequestDetailApproval"],
    "c-card": ["canValidateCardTx", "canAccessCreditCardModule", "canCreateCreditCardInvoice"],
    redactor: ["canRedact"],
    review: [],
    other: []
};

const ADMIN_SECTION_ACTIONS = new Set(ADMIN_SECTION_DEFINITIONS.map((section) => section.action));
const REMOVED_ADMIN_ACTIONS = new Set(["canAccessAdminShell", "canSeeClientsTab", "canSwitchClients"]);



const RolesEditForm = ({data, handleSubmit, updatedRole: role, setUpdatedRole: setRole, cancelForm, components, actionsList, actionsByComponents}) => {
    
    
    const moduleOptions = useMemo(
        () => (components || []).map((c) => ({id: c.id, value: {id:c.id, name:c.name}, label: c.name, _src: c})),
        [components]
    );
    
    const normalizePermittedModules = (permittedModulesLike) => {
        if (permittedModulesLike && !Array.isArray(permittedModulesLike) && typeof permittedModulesLike === "object") {
            return {
                limited_access: Boolean(permittedModulesLike.limited_access),
                modules: permittedModulesLike.modules || []
            };
        }
        const modules = Array.isArray(permittedModulesLike) ? permittedModulesLike : [];
        return {
            limited_access: false,
            modules
        };
    };

    const normalizeExpenseEditorConfig = (cfgLike) => {
        if (typeof cfgLike === "string") {
            return {
                variant: cfgLike || DEFAULT_EXPENSE_EDITOR_VARIANT,
                settings: { totals: { calculation_mode: DEFAULT_CALC_MODE } }
            };
        }
        const variant = cfgLike?.variant || DEFAULT_EXPENSE_EDITOR_VARIANT;
        const calcMode = cfgLike?.settings?.totals?.calculation_mode || DEFAULT_CALC_MODE;
        return { variant, settings: { totals: { calculation_mode: calcMode } } };
    };
    

    const getSelectedForRole = (role) => {
        // return a list of the permitted modules already present in the list
        const permitted = normalizePermittedModules(role?.permitted_modules);
        const ids = new Set((permitted.modules || []).map((m) => m.id));
        return moduleOptions.filter((o) => ids.has(o.id));
    };

    const groupedActions = useMemo(() => {
        const availableActions = new Set((actionsList || []).filter((action) => !REMOVED_ADMIN_ACTIONS.has(action)));
        const source =
            actionsByComponents && Object.keys(actionsByComponents).length
                ? actionsByComponents
                : DEFAULT_ACTIONS_BY_COMPONENT;

        const seen = new Set();
        const groups = [];

        Object.entries(source).forEach(([component, componentActions]) => {
            if (component === "admin") return;
            const filtered = (componentActions || []).filter((action) => availableActions.has(action));
            if (filtered.length > 0) {
                groups.push({
                    key: component,
                    label: `${(component)} Actions`,
                    actions: filtered
                });
                filtered.forEach((action) => seen.add(action));
            }
        });

        const remaining = (actionsList || []).filter((action) =>
            !seen.has(action) && !ADMIN_SECTION_ACTIONS.has(action) && !REMOVED_ADMIN_ACTIONS.has(action)
        );
        if (remaining.length > 0) {
            groups.push({
                key: "other",
                label: `${("other")} Actions`,
                actions: remaining
            });
        }

        return groups;
    }, [actionsList, actionsByComponents]);

    const adminTabOptions = useMemo(() => ADMIN_SECTION_DEFINITIONS.map((section) => ({
        id: section.action,
        value: section.action,
        label: data?.t?.read(section.translationKey) || section.key,
    })), [data?.t]);

    const selectedAdminTabs = adminTabOptions.filter((option) =>
        (role.authorized_actions || []).includes(option.value)
    );

    const setAdminTabs = (selectedOptions) => {
        const selectedActions = new Set((selectedOptions || []).map((option) => option.value));
        const adminActions = new Set(adminTabOptions.map((option) => option.value));
        const nextActions = (role.authorized_actions || []).filter((action) =>
            !adminActions.has(action) && !REMOVED_ADMIN_ACTIONS.has(action)
        );
        setRole({
            ...role,
            authorized_actions: Array.from(new Set([...nextActions, ...selectedActions])).sort(),
        });
    };

    const normalizeInvoiceEditorConfig = (cfgLike, withDefault = true) => {
        if (cfgLike == null) {
            return withDefault ? { variant: DEFAULT_INVOICE_EDITOR_VARIANT } : null;
        }
        if (typeof cfgLike === "string") {
            return { variant: cfgLike || DEFAULT_INVOICE_EDITOR_VARIANT };
        }
        const variant = cfgLike?.variant || DEFAULT_INVOICE_EDITOR_VARIANT;
        const boldFields = Array.isArray(cfgLike?.bold_fields) ? cfgLike.bold_fields : [];
        const hiddenFields = Array.isArray(cfgLike?.hidden_fields) ? cfgLike.hidden_fields : [];
        return { variant, bold_fields: boldFields, hidden_fields: hiddenFields };
    };
    
    const isPrimaryRole = Object.values(PRIMARY_ROLES).includes(role.name);

    // Role documents are stored unvalidated, so authorized_actions can arrive as a
    // bare string. `new Set("canRedact")` is a set of characters, so ticking any
    // box would have saved ["R","a","c",…] and destroyed the role's real
    // permissions. Reachable: XPEN-3377's backfill skips exactly these documents,
    // and re-ticking the box here is the recovery an operator is told to use.
    // AccessControl.hasActionAccess refuses the same shape (XPEN-3379).
    const authorizedActions = Array.isArray(role.authorized_actions) ? role.authorized_actions : [];

    const primaryRoleTranslationKey = role.name?.toLowerCase();
    const translatedRoleName =
        isPrimaryRole && primaryRoleTranslationKey
            ? data?.t?.read(`roles.${primaryRoleTranslationKey}`) || role.name
            : role.name;
    const displayRoleName = translatedRoleName || data?.t?.read("roles.new") || "New role";
    const permittedModules = normalizePermittedModules(role?.permitted_modules);

    const selected = getSelectedForRole(role);
    const expensesModule = (components || []).find((component) => component?.name === "expenses");
    const expenseViewOptions = (expensesModule?.options || [])
        .map(optionNameOf)
        .filter((optionName) => EXPENSE_VIEW_OPTIONS.includes(optionName));
    const roleExpenseViews = role?.view_configuration?.expenses?.views;
    const hasExplicitExpenseViewRestriction = Array.isArray(roleExpenseViews);
    const selectedExpenseViews = hasExplicitExpenseViewRestriction ? roleExpenseViews : expenseViewOptions;
    const invoiceBoldFieldOptions = INVOICE_BOLD_FIELD_OPTIONS.map((field) => ({
        id: field.id,
        value: field.id,
        label: data?.t?.read(field.labelKey) || field.fallbackLabel,
        _src: field,
    }));
    const invoiceHiddenFieldOptions = INVOICE_HIDDEN_FIELD_OPTIONS.map((field) => ({
        id: field.id,
        value: field.id,
        label: data?.t?.read(field.labelKey) || field.fallbackLabel,
        _src: field,
    }));
    const selectedInvoiceBoldFields = invoiceBoldFieldOptions.filter((option) =>
        normalizeInvoiceEditorConfig(role?.view_configuration?.invoice_editor)?.bold_fields?.includes(option.value)
    );
    const resolvedInvoiceHiddenFields = resolveInvoiceHiddenFields(
        normalizeInvoiceEditorConfig(role?.view_configuration?.invoice_editor)
    );
    const selectedInvoiceHiddenFields = invoiceHiddenFieldOptions.filter((option) =>
        resolvedInvoiceHiddenFields.includes(option.value)
    );

    const updateExpenseViews = (views) => {
        const vc = role.view_configuration || {};
        const nextExpensesConfig = { ...(vc.expenses || {}), views };
        setRole({ ...role, view_configuration: { ...vc, expenses: nextExpensesConfig } });
    };

    const clearExpenseViews = () => {
        const vc = role.view_configuration || {};
        const next = { ...vc };
        if (next.expenses) {
            const expensesConfig = { ...next.expenses };
            delete expensesConfig.views;
            if (Object.keys(expensesConfig).length > 0) {
                next.expenses = expensesConfig;
            } else {
                delete next.expenses;
            }
        }
        setRole({ ...role, view_configuration: next });
    };

    return (
        <form method="post" onSubmit={handleSubmit}>
            
            <Modal
                title={displayRoleName}
                okIsSubmit={true}
                okText={data.t.read("save")}
                width={"min(800px, 96vw)"}
                cancelText={data.t.read("cancel")}
                cancelAction={cancelForm}
            >
                {/* Name */}
                <div className={"form-group mb-3"}>
                    <label className="text-muted text-uppercase small d-block mb-1">
                        {data.t.read("name")}
                    </label>
                    <input
                        className={`form-control ${role.name ? "" : "border-highlight"}`}
                        required
                        disabled={isPrimaryRole}
                        placeholder={data?.t?.read("roles.name_placeholder") || "Enter role name"}
                        value={isPrimaryRole ? displayRoleName : role.name || ""}
                        onChange={(e) => setRole({...role, name: e.target.value})}
                        
                    />
                </div>
                
                {/*Authorized Module*/}
                <div className="mb-3">
                    <label className="text-muted text-uppercase small d-block mb-1">
                        {data?.t?.read("roles.permitted_modules_label") || "Permitted modules"}
                    </label>
                    <div className="text-muted small mb-2">{data?.t?.read("roles.permitted_modules_description") || "Control which modules/pages this role can access."}</div>
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={Boolean(normalizePermittedModules(role?.permitted_modules).limited_access)}
                                    onChange={(e) => {
                                        setRole({...role, permitted_modules:{...normalizePermittedModules(role.permitted_modules), limited_access: e.target.checked}})
                                    }}
                                    disabled={isPrimaryRole && !["Admin", "SuperAdmin"].includes(data.role)}
                                />
                            }
                            label={data?.t?.read("roles.permitted_modules_limited_access") || "Limit access to the selected modules"}
                        />
                    </FormGroup>
                    <MultiSelectSort
                        options={moduleOptions}
                        value={selected}
                        selected={selected}
                        setSelected={(newList) => {
                            const modules = newList.map((m) => m.value);
                            setRole({...role, permitted_modules:{...normalizePermittedModules(role.permitted_modules), modules: modules}})
                        }}
                        locked={!permittedModules.limited_access || (isPrimaryRole && !["Admin", "SuperAdmin"].includes(data.role))}
                        placeholder={data?.t?.read("roles.permitted_modules_placeholder") || "Select permitted modules"}
                        styles={
                            permittedModules.limited_access
                                ? {}
                                : {control: (base) => ({...base, backgroundColor: "#f8f9fa"})}
                        }
                    />
                    {!permittedModules.limited_access ? (
                        <div className="text-muted small mt-1">{data?.t?.read("roles.permitted_modules_full_access_hint") || "When disabled, the role can access all modules."}</div>
                    ) : null}
                </div>
                
                {/*Universal Proxy*/}
                <div className="mb-3">
                    <label className="text-muted text-uppercase small d-block mb-1">
                        {data?.t?.read("roles.universal_proxy_label") || "Universal proxy"}
                    </label>
                    <div className="text-muted small mb-2">
                        {data?.t?.read("roles.universal_proxy_description") ||
                            "Allows users with this role to act as assistants to active users in this firm without per-user assignment."}
                    </div>
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={Boolean(role?.universalProxy)}
                                    onChange={(e) => setRole({...role, universalProxy: e.target.checked})}
                                    disabled={!["Admin", "SuperAdmin"].includes(data.role)}
                                />
                            }
                            label={data?.t?.read("roles.universal_proxy_toggle") || "Enable universal proxy"}
                        />
                    </FormGroup>
                </div>

                {/*Admin tabs access*/}
                <div className="mb-3">
                    <label className="text-muted text-uppercase small d-block mb-1">
                        {data?.t?.read("roles.admin_tabs_access_label") || "Admin tabs access"}
                    </label>
                    <MultiSelectSort
                        value={selectedAdminTabs}
                        selected={selectedAdminTabs}
                        setSelected={setAdminTabs}
                        options={adminTabOptions}
                        locked={isPrimaryRole && !["Admin", "SuperAdmin"].includes(data.role)}
                        placeholder={data?.t?.read("roles.admin_tabs_access_placeholder") || "Select Admin tabs"}
                    />
                </div>

                {/*Authorized Actions*/}
                <div className="mb-3">
                    <label className="text-muted text-uppercase small d-block mb-1">
                        {data?.t?.read("roles.authorized_actions_label") || "Authorized actions"}
                    </label>
                    <div className="text-muted small mb-2">{data?.t?.read("roles.authorized_actions_description") ||
                        "Grant or remove access to advanced application actions for this role."}
                    </div>

                    <FormGroup>
                        <Grid container>
                            {groupedActions.map((group) => (
                                <Grid item xs={12} key={group.key}>
                                    <Typography variant="subtitle2" className="font-weight-bold text-muted mb-1">
                                        {group.label}
                                    </Typography>
                                    <Grid container>
                                        {group.actions.map((actionKey) => (
                                            <Grid item  key={actionKey} sx={{py: 0, px: 0}}>
                                                <FormControlLabel
                                                    sx={{mt: 0, mb: 0, mr: 2}}
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            checked={authorizedActions.includes(actionKey)}
                                                            onChange={(e) => {
                                                                const current = new Set(authorizedActions);
                                                                if (e.target.checked) current.add(actionKey);
                                                                else current.delete(actionKey);
                                                                setRole({...role, authorized_actions: Array.from(current).sort()});
                                                            }
                                                            }
                                                            disabled={isPrimaryRole && !["Admin", "SuperAdmin"].includes(data.role)}
                                                        />
                                                    }
                                                    label={data?.t?.read(`app_actions.${actionKey}`) || actionKey}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Grid>
                            ))}
                        </Grid>
                    </FormGroup>
                </div>

                {/* ---- View Configuration ---- */}
                <div className="mt-3">
                    <label className="text-muted text-uppercase small d-block mb-1">
                        {data?.t?.read("roles.view_configuration") || "View Configuration"}
                    </label>

                    <div className="row">
                        <div className="col-12">
                            <label className="font-weight-bold d-block mb-1">{data?.t?.read("roles.view_configuration_expense_editor_header") || "Expense Editor"}</label>
                            <div className="text-muted small mb-3">{data?.t?.read("roles.view_configuration_expense_editor_header_desc") ||
                                "Configure how the expense editor behaves for this role."}
                            </div>
                        </div>

                        {expenseViewOptions.length > 0 && (
                            <div className="col-12 mb-3">
                                <label className="font-weight-bold d-block mb-1">{data?.t?.read("roles.view_configuration_expense_views_header") || "Expense views"}</label>
                                <div className="text-muted small mb-2">{data?.t?.read("roles.view_configuration_expense_views_desc") ||
                                    "Restrict this role to a subset of the expense views enabled for the client."}
                                </div>
                                <FormGroup>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={hasExplicitExpenseViewRestriction}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        updateExpenseViews(expenseViewOptions);
                                                    } else {
                                                        clearExpenseViews();
                                                    }
                                                }}
                                            />
                                        }
                                        label={data?.t?.read("roles.view_configuration_expense_views_limit") || "Limit expense views for this role"}
                                    />
                                </FormGroup>
                                {hasExplicitExpenseViewRestriction && (
                                    <FormGroup>
                                        <Grid container>
                                            {expenseViewOptions.map((viewName) => (
                                                <Grid item key={viewName} sx={{py: 0, px: 0}}>
                                                    <FormControlLabel
                                                        sx={{mt: 0, mb: 0, mr: 2}}
                                                        control={
                                                            <Checkbox
                                                                size="small"
                                                                checked={selectedExpenseViews.includes(viewName)}
                                                                onChange={(e) => {
                                                                    const current = new Set(selectedExpenseViews);
                                                                    if (e.target.checked) current.add(viewName);
                                                                    else current.delete(viewName);
                                                                    updateExpenseViews(expenseViewOptions.filter((option) => current.has(option)));
                                                                }}
                                                            />
                                                        }
                                                        label={data?.t?.read(`module_options.${viewName}`) || data?.t?.read(viewName) || viewName}
                                                    />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </FormGroup>
                                )}
                            </div>
                        )}
                        
                        <div className="col-12 col-md-5 col-lg-4">
                            <label className="font-weight-bold d-block mb-1">{data?.t?.read("roles.view_configuration_expense_editor_variant") || "Variant"}</label>
                            <div className="text-muted small mb-2">{data?.t?.read("roles.view_configuration_expense_editor_variant_desc") ||
                                "Choose the overall layout: Normal or Simplified."}
                            </div>
                            <RadioButtonsGroup
                                options={
                                [
                                    { value: "normal", label: data?.t?.read("roles.view_configuration_expense_editor_option_normal") || "Normal" },
                                    { value: "simplified", label: data?.t?.read("roles.view_configuration_expense_editor_option_simplified") || "Simplified" }
                                ]
                            }
                                value={normalizeExpenseEditorConfig(role?.view_configuration?.expense_editor)?.variant}
                                onChange={(val) => {
                                    const vc = role.view_configuration || {};
                                    const ee = normalizeExpenseEditorConfig(vc.expense_editor);
                                    const next = { ...vc, expense_editor: { ...ee, variant: val } };
                                    setRole({...role, view_configuration: next});
                                }}
                                row
                            />
                        </div>
                    
                        {normalizeExpenseEditorConfig(role?.view_configuration?.expense_editor)?.variant === "simplified" && (
                            <div className="col-12 col-md-7 col-lg-6">
                                <label className="font-weight-bold d-block mb-1">{data?.t?.read("roles.view_configuration_totals_label") || "Totals behavior"}</label>
                                <div className="text-muted small mb-2">{data?.t?.read("roles.view_configuration_totals_desc") ||
                                    "Decide how totals are handled in Simplified mode."}</div>
                                <RadioButtonsGroup
                                    options={
                                        [
                                            { value: "auto_adjusted", label: data?.t?.read("roles.view_configuration_totals_auto_adjusted") || "Auto-adjusted totals" },
                                            { value: "raw", label: data?.t?.read("roles.view_configuration_totals_raw") || "Raw totals" }
                                        ]
                                    }
                                    value={normalizeExpenseEditorConfig(role?.view_configuration?.expense_editor)?.settings?.totals?.calculation_mode || DEFAULT_CALC_MODE}
                                    onChange={(val) => {
                                        const vc = role.view_configuration || {};
                                        const ee = normalizeExpenseEditorConfig(vc.expense_editor);
                                        const next = {
                                            ...vc,
                                            expense_editor: {
                                                ...ee,
                                                settings: {
                                                    ...ee.settings,
                                                    totals: { ...(ee.settings?.totals || {}), calculation_mode: val }
                                                }
                                            }
                                        };
                                        setRole({...role, view_configuration: next});
                                    }}
                                    row
                                />
                                <div className="text-muted small mt-1">
                                    {normalizeExpenseEditorConfig(role?.view_configuration?.expense_editor)?.settings?.totals?.calculation_mode || DEFAULT_CALC_MODE === "raw" ? data?.t?.read("roles.view_configuration_totals_hint_raw") ||
                                        "Users can enter Total and Tip directly. Tax fields stay empty for accounting." : data?.t?.read("roles.view_configuration_totals_hint_auto") ||
                                        "System applies auto adjustments for totals and taxes."}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="row mt-3">
                        <div className="col-12">
                            <label className="font-weight-bold d-block mb-1">{data?.t?.read("roles.view_configuration_invoice_editor_header") || "Invoice Editor"}</label>
                            <div className="text-muted small mb-3">{data?.t?.read("roles.view_configuration_invoice_editor_header_desc") ||
                                "Configure how the invoice editor behaves for this role."}</div>
                        </div>
                    
                        <div className="col-12 col-md-5 col-lg-4">
                            <label className="font-weight-bold d-block mb-1">{data?.t?.read("roles.view_configuration_invoice_editor_variant") || "Variant"}</label>
                            <div className="text-muted small mb-2">{data?.t?.read("roles.view_configuration_invoice_editor_variant_desc") ||
                                "Choose the overall layout: Normal or Focused."}</div>
                            <RadioButtonsGroup
                                options={
                                    [
                                        { value: "normal", label: data?.t?.read("roles.view_configuration_invoice_editor_option_normal") || "Normal" },
                                        { value: "focused", label: data?.t?.read("roles.view_configuration_invoice_editor_option_focused") || "Focused" }
                                    ]
                                }
                                value={normalizeInvoiceEditorConfig(role?.view_configuration?.invoice_editor)?.variant}
                                onChange={(val) => {
                                    const vc = role.view_configuration || {};
                                    const ie = vc.invoice_editor;
                                    const next = { ...vc, invoice_editor: { ...ie, variant: val } };
                                    setRole({...role, view_configuration: next});
                                }}
                                row
                            />
                        </div>
                        <div className="col-12 mt-3">
                            <label className="font-weight-bold d-block mb-1">{data?.t?.read("roles.view_configuration_invoice_bold_fields") || "Bold invoice fields"}</label>
                            <div className="text-muted small mb-2">{data?.t?.read("roles.view_configuration_invoice_bold_fields_desc") ||
                                "Select invoice labels that should be bold for this role. Leave empty to use the default fields."}</div>
                            <MultiSelectSort
                                options={invoiceBoldFieldOptions}
                                value={selectedInvoiceBoldFields}
                                selected={selectedInvoiceBoldFields}
                                setSelected={(newList) => {
                                    const vc = role.view_configuration || {};
                                    const ie = normalizeInvoiceEditorConfig(vc.invoice_editor);
                                    const boldFields = (newList || []).map((field) => field.value);
                                    const next = { ...vc, invoice_editor: { ...ie, bold_fields: boldFields } };
                                    setRole({...role, view_configuration: next});
                                }}
                                placeholder={data?.t?.read("roles.view_configuration_invoice_bold_fields_placeholder") || "Select bold invoice fields"}
                            />
                            <div className="text-muted small mt-1">
                                {data?.t?.read("roles.view_configuration_invoice_bold_fields_default_hint") ||
                                    "No selected fields uses the default bold labels."}
                            </div>
                        </div>
                        <div className="col-12 mt-3">
                            <label className="font-weight-bold d-block mb-1">{data?.t?.read("roles.view_configuration_invoice_hidden_fields") || "Hidden invoice fields"}</label>
                            <div className="text-muted small mb-2">{data?.t?.read("roles.view_configuration_invoice_hidden_fields_desc") ||
                                "Select invoice fields that should be hidden for this role."}</div>
                            <MultiSelectSort
                                options={invoiceHiddenFieldOptions}
                                value={selectedInvoiceHiddenFields}
                                selected={selectedInvoiceHiddenFields}
                                setSelected={(newList) => {
                                    const vc = role.view_configuration || {};
                                    const ie = normalizeInvoiceEditorConfig(vc.invoice_editor);
                                    const hiddenFields = (newList || []).map((field) => field.value);
                                    const next = { ...vc, invoice_editor: { ...ie, hidden_fields: hiddenFields } };
                                    setRole({...role, view_configuration: next});
                                }}
                                placeholder={data?.t?.read("roles.view_configuration_invoice_hidden_fields_placeholder") || "Select hidden invoice fields"}
                            />
                        </div>
                    </div>
                </div>
                

            </Modal>
        </form>
    )
}

export default RolesEditForm;
